import { useState } from "react"
import { CircularProgress, Collapse, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { If, Else } from "lib/@components/switch"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useReferenceState } from "lib/@hooks/use-reference"
import { getAuthoringLegislationLinkHealth } from "../controller/get-authoring-legislation-link-health"

const LABEL_SX = { color: "text.secondary", fontWeight: 600, letterSpacing: 0.5 }

const RESULT = {
    green: { icon: "mdi:check-circle", color: "success.main", title: "Healthy" },
    amber: { icon: "mdi:alert-circle", color: "warning.main", title: "Needs attention" },
    red: { icon: "mdi:close-circle", color: "error.main", title: "Broken link" },
}

export function LegislationLinkHealthSection({ id }) {
    const [dismissedMessage, setDismissedMessage] = useReferenceState(`legislation-link-health-dismiss:${id}`, null)
    const health = getAuthoringLegislationLinkHealth.useResults({ id }, withInitialValue(IS_LOADING))
    const [expanded, setExpanded] = useState(false)

    const loading = health === IS_LOADING
    if (!loading && !health) return null

    const isDismissed = !loading && dismissedMessage === health.message
    const dismissedHealthy = isDismissed && health.level === "green"
    const canDismiss = !loading && !isDismissed && health.level !== "red"
    const result = loading ? null : RESULT[health.level] ?? RESULT.amber

    return (
        <>
            <Divider sx={{ my: 3 }} />
            <Stack spacing={1} data-cy="legislation-link-health">
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 34 }}>
                    <Typography variant="overline" sx={LABEL_SX}>
                        LINK HEALTH CHECK
                    </Typography>
                    <If value={canDismiss}>
                        <Tooltip title="Dismiss">
                            <IconButton
                                size="small"
                                onClick={dismiss}
                                aria-label="Dismiss link health check"
                                data-cy="legislation-link-health-dismiss"
                            >
                                <Iconify icon="mdi:close" sx={{ width: 18, height: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </If>
                </Stack>

                <If value={loading}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ py: 0.5 }}
                        data-cy="legislation-link-health-loading"
                    >
                        <CircularProgress size={18} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            <If value={dismissedMessage}>
                                Rechecking previously dismissed link…
                                <Else>Link health check in progress…</Else>
                            </If>
                        </Typography>
                    </Stack>
                    <Else>
                        <If value={isDismissed}>
                            <Stack data-cy="legislation-link-health-reconfirmed">
                                <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 0.5 }}>
                                    <Iconify
                                        icon="mdi:check-circle"
                                        sx={{ width: 22, height: 22, color: "success.main", flexShrink: 0, mt: 0.25 }}
                                    />
                                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ color: "success.main" }}>
                                            <If value={dismissedHealthy}>
                                                Healthy
                                                <Else>Reviewed</Else>
                                            </If>
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            <If value={dismissedHealthy}>
                                                Rechecked, link still healthy.
                                                <Else>Rechecked, no change since you dismissed this warning.</Else>
                                            </If>
                                        </Typography>
                                    </Stack>
                                    <If value={!dismissedHealthy}>
                                        <IconButton
                                            size="small"
                                            onClick={toggleExpanded}
                                            aria-label={expanded ? "Hide dismissed warning" : "Show dismissed warning"}
                                            aria-expanded={expanded}
                                            data-cy="legislation-link-health-reconfirmed-toggle"
                                            sx={{ mt: -0.25, flexShrink: 0 }}
                                        >
                                            <Iconify
                                                icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                                                sx={{ width: 20, height: 20 }}
                                            />
                                        </IconButton>
                                    </If>
                                </Stack>
                                <If value={!dismissedHealthy}>
                                    <Collapse in={expanded} timeout={0} unmountOnExit>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "text.secondary", pl: "34px", pb: 0.5 }}
                                            data-cy="legislation-link-health-reconfirmed-message"
                                        >
                                            {dismissedMessage}
                                        </Typography>
                                    </Collapse>
                                </If>
                            </Stack>
                            <Else>
                                <Stack
                                    direction="row"
                                    alignItems="flex-start"
                                    spacing={1.5}
                                    sx={{ py: 0.5 }}
                                    data-cy="legislation-link-health-result"
                                >
                                    <Iconify
                                        icon={result?.icon}
                                        sx={{ width: 22, height: 22, color: result?.color, flexShrink: 0, mt: 0.25 }}
                                    />
                                    <Stack sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ color: result?.color }}>
                                            {result?.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            {health?.message}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Else>
                        </If>
                    </Else>
                </If>
            </Stack>
        </>
    )

    function dismiss() {
        setDismissedMessage(health.message)
    }

    function toggleExpanded() {
        setExpanded((open) => !open)
    }
}
